import Phaser from 'phaser';
import { MainMenuScene } from './scenes/MainMenuScene';
import { GamePlayScene } from './scenes/GamePlayScene';
import { GameOverScene } from './scenes/GameOverScene';
import { SettingsScene } from './scenes/SettingsScene';
import { EarthSelectionScene } from './scenes/EarthSelectionScene';
import { ProfileScene } from './scenes/ProfileScene';
import { AchievementScene } from './scenes/AchievementScene';
import { LeaderboardScene } from './scenes/LeaderboardScene';
import { loadUserProfile, saveUserProfile, UserProfile } from './entities/base/UserProfile';
import { MobileUtils } from './utils/MobileUtils';
import { ShopScene } from './scenes/ShopScene';
import { InventoryScene } from './scenes/InventoryScene';

export interface GameConfig {
  width: number;
  height: number;
  parent: string;
}

export class Game extends Phaser.Game {
  private userProfile: UserProfile;

  constructor() {
    // Get optimal game size for current device
    const optimalSize = MobileUtils.getOptimalGameSize();
    const deviceInfo = MobileUtils.getDeviceInfo();
    const performanceSettings = MobileUtils.getPerformanceSettings();
    
    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      width: optimalSize.width,
      height: optimalSize.height,
      parent: 'game-container',
      scene: [
        MainMenuScene,
        EarthSelectionScene,
        ProfileScene,
        AchievementScene,
        LeaderboardScene,
        GamePlayScene,
        GameOverScene,
        SettingsScene,
        InventoryScene,
        ShopScene,
      ],
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 0, x: 0 },
          debug: false,
        },
      },
      backgroundColor: '#000033',
      scale: {
        mode: deviceInfo.isMobile ? Phaser.Scale.FIT : Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        min: {
          width: deviceInfo.isMobile ? 320 : 800,
          height: deviceInfo.isMobile ? 480 : 600,
        },
        max: {
          width: deviceInfo.isMobile ? 480 : 1920,
          height: deviceInfo.isMobile ? 854 : 1080,
        },
      },
      render: {
        pixelArt: false,
        antialias: performanceSettings.enableAntialiasing,
        powerPreference: deviceInfo.isMobile ? 'low-power' : 'high-performance',
      },
      fps: {
        target: performanceSettings.targetFPS,
        forceSetTimeOut: deviceInfo.isMobile,
      },
      input: {
        touch: deviceInfo.touchSupported,
        mouse: !deviceInfo.isMobile,
        keyboard: !deviceInfo.isMobile,
      },
    };

    super(config);
    
    // Apply device-specific optimizations
    this.applyDeviceOptimizations();
    
    // Load user profile at game start
    this.initializeProfile();
    
    // Log device and performance info
    console.log('Game initialized with configuration:', {
      device: deviceInfo,
      gameSize: optimalSize,
      performance: performanceSettings,
    });
  }

  private applyDeviceOptimizations(): void {
    const deviceInfo = MobileUtils.getDeviceInfo();
    const performanceSettings = MobileUtils.getPerformanceSettings();
    
    if (deviceInfo.isMobile) {
      // Mobile-specific optimizations
      
      // Reduce object pool sizes for better memory management
      (window as any).MOBILE_OPTIMIZATIONS = {
        maxParticles: performanceSettings.maxParticles,
        maxProjectiles: performanceSettings.maxProjectiles,
        useObjectPooling: performanceSettings.useObjectPooling,
        enableShadows: performanceSettings.enableShadows,
      };
      
      // Adjust texture quality
      this.textures.on('addtexture', (key: string, texture: Phaser.Textures.Texture) => {
        // Could implement texture scaling for mobile here
        if (deviceInfo.devicePixelRatio > 1) {
          // High DPI device - might want to scale down textures
        }
      });
      
      // Battery optimization - reduce frame rate when page is not visible
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.loop.targetFps = 15; // Reduce FPS when hidden
        } else {
          this.loop.targetFps = performanceSettings.targetFPS;
        }
      });
    }
    
    // Memory management
    this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      // Handle responsive resize
      console.log('Game resized:', gameSize);
    });
  }

  private initializeProfile(): void {
    this.userProfile = loadUserProfile();
    
    // Make profile available globally
    (window as any).userProfile = this.userProfile;
    
    console.log('Game initialized with user profile:', this.userProfile);
  }

  // Helper method to update profile from any scene
  public updateProfile(updates: Partial<UserProfile>): void {
    this.userProfile = { ...this.userProfile, ...updates };
    (window as any).userProfile = this.userProfile;
    saveUserProfile(this.userProfile);
  }

  // Get device-specific UI scaling
  public getUIScale(): number {
    return MobileUtils.scaleForDevice(1.0);
  }

  // Get device info for scenes
  public getDeviceInfo(): any {
    return MobileUtils.getDeviceInfo();
  }

  // Handle orientation changes
  public onOrientationChange(): void {
    const newDeviceInfo = MobileUtils.getDeviceInfo();
    const newOptimalSize = MobileUtils.getOptimalGameSize();
    
    // Notify all active scenes of orientation change
    this.scene.scenes.forEach(scene => {
      if (scene.scene.isActive() && 'onOrientationChange' in scene) {
        (scene as any).onOrientationChange(newDeviceInfo, newOptimalSize);
      }
    });
  }

  // Pause game for mobile battery optimization
  public pauseForBackground(): void {
    const deviceInfo = MobileUtils.getDeviceInfo();
    if (deviceInfo.isMobile) {
      // this.scene.pause();
      this.sound.pauseAll();
      this.loop.targetFps = 1; // Minimal FPS
    }
  }

  // Resume game when returning to foreground
  public resumeFromBackground(): void {
    const deviceInfo = MobileUtils.getDeviceInfo();
    if (deviceInfo.isMobile) {
      // this.scene.resume();
      this.sound.resumeAll();
      const performanceSettings = MobileUtils.getPerformanceSettings();
      this.loop.targetFps = performanceSettings.targetFPS;
    }
  }
} 